import React, { useEffect, useState, useRef } from 'react';
import { ClockIcon, ChevronDownIcon } from 'lucide-react';
interface DurationPickerDropdownProps {
  value: number;
  onChange: (value: number) => void;
  darkMode?: boolean;
  required?: boolean;
  error?: boolean;
}
export const DurationPickerDropdown: React.FC<DurationPickerDropdownProps> = ({
  value,
  onChange,
  darkMode = false,
  required = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Generate duration options: 5 to 120 minutes in 5-minute increments
  const durationOptions = [];
  for (let i = 5; i <= 120; i += 5) {
    durationOptions.push(i);
  }
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Scroll to selected value when dropdown opens
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedIndex = durationOptions.indexOf(value);
      if (selectedIndex !== -1) {
        const itemHeight = 40; // Height of each item in pixels
        const scrollPosition = selectedIndex * itemHeight - itemHeight * 2; // Center the selected item
        listRef.current.scrollTop = Math.max(0, scrollPosition);
      }
    }
  }, [isOpen, value, durationOptions]);
  const handleSelect = (duration: number) => {
    onChange(duration);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1347">
      {/* Input Display */}
      <div onClick={() => setIsOpen(!isOpen)} className={`w-full pl-11 pr-10 py-2.5 rounded-lg cursor-pointer flex items-center justify-between ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} border ${error ? 'border-red-500 focus:border-red-500' : 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20'} shadow-sm transition-all`} data-id="element-1348">
        <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-1349">
          <ClockIcon className="h-4 w-4 text-[#BA68C8]" data-id="element-1350" />
        </div>
        <span data-id="element-1351">{value}</span>
        <div className="absolute inset-y-0 right-3.5 flex items-center pointer-events-none" data-id="element-1352">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`} data-id="element-1353">
            min
          </span>
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1354" />
        </div>
      </div>
      {/* Dropdown List */}
      {isOpen && <div className={`absolute z-50 w-full mt-2 rounded-lg shadow-xl ${darkMode ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'}`} style={darkMode ? {
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
    } : {
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
    }} data-id="element-1355">
          <ul ref={listRef} className="py-1 overflow-y-auto" style={{
        maxHeight: '240px' // 6 items × 40px = 240px
      }} data-id="element-1356">
            {durationOptions.map(duration => <li key={duration} onClick={() => handleSelect(duration)} className={`px-4 py-2.5 cursor-pointer transition-colors ${duration === value ? darkMode ? 'bg-navy-700/50 text-white' : 'bg-gray-100 text-gray-900' : darkMode ? 'text-white hover:bg-navy-700/30' : 'text-gray-900 hover:bg-gray-50'}`} style={{
          height: '40px',
          display: 'flex',
          alignItems: 'center'
        }} data-id="element-1357">
                <span className="text-sm" data-id="element-1358">{duration} min</span>
              </li>)}
          </ul>
        </div>}
      <style jsx data-id="element-1359">{`
        ul::-webkit-scrollbar {
          width: 6px;
        }
        ul::-webkit-scrollbar-track {
          background: transparent;
        }
        ul::-webkit-scrollbar-thumb {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
          border-radius: 3px;
        }
        ul::-webkit-scrollbar-thumb:hover {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
        }
      `}</style>
    </div>;
};